import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2
} from '@angular/core';
import {CommonModule, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {QuillEditorComponent} from 'ngx-quill';
import {EditableQuillHtmlDirective} from '../../../../../core/directives/EditableQuillHtmlDirective';
import {QuillConfiguration, QuillFormats, setupClipboardInterceptors} from '../../../../../../environments/fixed-enum';
import {Subject, takeUntil} from 'rxjs';
import {RestFactoryService} from '../../../../../core/services/rest-factory.service';
import {FuseConfirmationService} from '../../../../../../@fuse/services/confirmation';
import Quill from 'quill';

@Component({
    selector: 'app-products-tab',
    templateUrl: './products-tab.component.html',
    styleUrls: ['./products-tab.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        NgIf,
        NgForOf,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        QuillEditorComponent,
        EditableQuillHtmlDirective,
    ],
})
export class ProductsTabComponent implements OnInit, OnDestroy {
    @Input() productGroup: any;
    @Input() destinationId: string;
    @Input() reviewMode: boolean = false;
    @Output() save = new EventEmitter<void>();

    selectedProduct: any = null;
    typingTimeout: any;
    quillModules: any = QuillConfiguration;
    quillFormats: any[] = QuillFormats;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private API_POST_PRODUCT = "/admin/destination/productadd";
    private API_POST_PRODUCT_IMAGE = "/admin/destination/productimageadd";

    constructor(
        private _restFactoryService: RestFactoryService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _renderer: Renderer2,
        private _el: ElementRef
    ) {
    }

    ngOnInit() {
        if (this.productGroup?.ar_items?.length > 0) {
            this.selectedProduct = this.productGroup.ar_items[0];
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    selectProduct(product: any): void {
        this.selectedProduct = product;
    }

    onKeyupAutosave(): void {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            this.save.emit();
        }, 500);
    }

    onEditorCreated(quillInstance: Quill): void {
        setupClipboardInterceptors(quillInstance);
    }

    removeProduct(productToDelete: any, event: MouseEvent): void {
        event.stopPropagation();

        if (!productToDelete) {
            return;
        }

        this._fuseConfirmationService.open({
            title: "Warning!",
            message: `The item "${productToDelete.ds_name || 'Untitled'}" will be deleted!`,
            actions: {confirm: {show: true}, cancel: {show: true}},
            dismissible: true
        }).afterClosed().subscribe((result) => {
            if (result === "confirmed") {
                const index = this.productGroup.ar_items.findIndex(item => item === productToDelete);
                if (index > -1) {
                    this.productGroup.ar_items.splice(index, 1);

                    if (this.selectedProduct === productToDelete) {
                        this.selectedProduct = null;
                    }

                    this.save.emit();
                    this._changeDetectorRef.markForCheck();
                }
            }
        });
    }

    addProduct(): void {
        const payload = new FormData();
        payload.append("id_producttype", this.productGroup.id_producttype);
        payload.append("id_destination", this.destinationId);

        this._restFactoryService.httpPostService(this.API_POST_PRODUCT, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.productGroup.ar_items.push(response.data);
                this.selectProduct(response.data);
                this._changeDetectorRef.markForCheck();
            });
    }

    onFileInputClick(productId: string): void {
        const fileInput = this._el.nativeElement.querySelector(`#FileUpload${productId}`);
        this._renderer.selectRootElement(fileInput).click();
    }

    onChangeFile(event: any, product: any): void {
        const file = (event.target as HTMLInputElement).files[0];
        if (!file) return;

        const payload = new FormData();
        payload.append("im_image", file);
        payload.append("id_destination", this.destinationId);
        payload.append("id_destinationproduct", product.id_destinationproduct);

        this._restFactoryService.httpPostService(this.API_POST_PRODUCT_IMAGE, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                product.im_media = response.im_media;
                this._changeDetectorRef.markForCheck();
            });
    }
}
